/**
 * 全站分级阈值（唯一前端出口）。
 *
 * 权威源在后端（pms-go-api tangentindex 的 grade* 常量），经 GET /tangent-index/grade-config 下发。
 * 前端着色/分级不再各处硬编码，一律从这里读，避免前后端漂移（历史上 RQI/PPI 阈值就因两处各写一份而漂过）。
 *
 * 每组为 3 个边界值：
 *   - IRI（越小越好，上界不含）：[优, 良, 中]
 *   - PCI/PPI（越大越好，≥ 边界）：[优, 良, 中]
 *   - RQI（≤ 边界，升序，越小越差）：[差, 中, 良]
 *
 * 默认值 = 后端当前值，作为接口未就绪/失败时的兜底；应用启动时 loadGradeConfig() 会用后端值覆盖。
 * 消费方在“调用时”读取字段，因此后端值加载完成后即自动生效，无需重新挂载。
 */
import { tangentIndexApi } from '@/api/tangentIndex'

type Triple = [number, number, number]

export interface GradeConfig {
  iriExpressway: Triple
  iriNormal: Triple
  pciExpressway: Triple
  pciNormal: Triple
  rqi: Triple
  ppi: Triple
}

// 可变单例：消费方按引用读取字段，loadGradeConfig 就地覆盖即可全站生效。
export const gradeConfig: GradeConfig = {
  // 高速与 Major/Minor Road 统一使用截图标准，单位 m/km，边界保留一位小数。
  iriExpressway: [3.6, 4.6, 5.7],
  iriNormal: [3.6, 4.6, 5.7],
  pciExpressway: [85, 70, 60],
  pciNormal: [85, 70, 60],
  rqi: [30, 50, 70],
  ppi: [85, 75, 60],
}

let loaded = false

// 变更订阅：地图着色等在“调用时”读字段，天然拿到新值；但 React 组件（如首页图例）
// 把阈值渲染成文案后就定格了，需要在后端值覆盖后重渲染，否则会一直显示兜底默认值。
type Listener = () => void
const listeners = new Set<Listener>()
let version = 0

export function subscribeGradeConfig(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getGradeConfigVersion(): number {
  return version
}

// 从后端拉取并覆盖本地默认值；失败时静默沿用默认值（与后端一致），不阻塞渲染。幂等：多次调用只真正请求一次。
export async function loadGradeConfig(): Promise<void> {
  if (loaded) return
  try {
    const cfg = await tangentIndexApi.getGradeConfig()
    if (cfg && Array.isArray(cfg.ppi) && cfg.ppi.length === 3) {
      gradeConfig.iriExpressway = cfg.iri_expressway
      gradeConfig.iriNormal = cfg.iri_normal
      gradeConfig.pciExpressway = cfg.pci_expressway
      gradeConfig.pciNormal = cfg.pci_normal
      gradeConfig.rqi = cfg.rqi
      gradeConfig.ppi = cfg.ppi
      loaded = true
      version += 1
      listeners.forEach((listener) => listener())
    }
  } catch {
    // 保底：沿用默认值（与后端一致）。下次调用可再试。
  }
}
